import { useAppStore } from '~/stores'
import { Button, Popover, PopoverContent, PopoverTrigger, Switch, showDialog } from '@bpinternal/ui-kit-next'
import { Sparkles, Loader2 } from 'lucide-react'
import { cn } from '~/utils'
import AiBannerGradient from '~/assets/ai-banner-gradient.svg?react'
import AiBannerGradientMobile from '~/assets/ai-banner-gradient-mobile.svg?react'
import { Spinner, Badge } from '~/elements'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useInsightsPlugin } from '../hooks/useInsightsPlugin'
import { useEffect, useState } from 'react'
import { AnnouncementsSearch } from '~/features/announcements/types'
import { PublishWarningDialog } from '~/features/conversations/components'
import { showPlanSelectionDialog } from '~/features/billing/components/PlanSelectionDialog'

type Props = {
  workspaceId: string
  botId: string
  isMobile: boolean
  insightsPluginState: ReturnType<typeof useInsightsPlugin>
}

export const AiButton = ({ isMobile, insightsPluginState, ...props }: Props) => {
  const { isAiEnabled, isPublishing, isBotUpdating, enableAiInsights, insightsPlugin } = insightsPluginState

  const activeWorkspace = useAppStore((state) => state.activeWorkspace)
  const needPlanUpgrade = activeWorkspace?.plan === 'community'
  const [open, setOpen] = useState(false)

  const search = useSearch({ strict: false }) as AnnouncementsSearch
  const navigate = useNavigate()

  useEffect(() => {
    if (search.aiInsights === true) {
      setOpen(true)
      navigate({
        search: undefined,
        replace: true,
      })
    }
  }, [])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button className={cn(isAiEnabled ? 'ai-enabled' : 'enable-ai', 'flex w-9 items-center gap-2 md:w-32')}>
          {isBotUpdating || isPublishing ? <Loader2 className="animate-spin" /> : <Sparkles />}
          {!isMobile && (isAiEnabled ? 'AI Enabled' : 'Enable AI')}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="flex h-72 w-96 flex-col overflow-hidden rounded-2xl p-0 md:h-52 md:w-[415px] md:flex-row"
        align={isMobile ? 'end' : 'start'}
      >
        {isMobile ? (
          <div className="h-20 w-full overflow-hidden">
            <AiBannerGradientMobile className="w-full scale-105" />
          </div>
        ) : (
          <div className="h-full w-44 overflow-hidden">
            <AiBannerGradient className="h-full scale-110" />
          </div>
        )}

        <div className="flex flex-col gap-2 p-6">
          <h1 className="flex items-center gap-3 text-xl font-semibold">
            Capture AI Insights
            <Badge variant="secondary" color="blue" className="rounded-full">
              Beta
            </Badge>
          </h1>
          {needPlanUpgrade ? (
            <div className="flex h-full flex-col justify-between">
              <p className="text-sm">
                Upgrade your plan to get sentiment analysis and AI summaries for incoming conversations.
              </p>
              <Button className="w-fit" onClick={() => showPlanSelectionDialog({ workspaceId: props.workspaceId })}>
                Upgrade to Plus
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm">
                Get quick sentiment analysis and concise AI summaries for incoming conversations.
              </p>
              <p className="text-sm text-muted-foreground">This feature will incur additional AI Cost</p>
              <div className="mt-2 flex justify-between ">
                <p className="text-sm font-medium">Enable Conversation Insights</p>
                {isBotUpdating || isPublishing ? (
                  <Spinner className="w-9" size="3" color="gray" />
                ) : (
                  <Switch
                    className="w-9"
                    checked={isAiEnabled || false}
                    disabled={isBotUpdating || isPublishing || !insightsPlugin?.enabled}
                    onCheckedChange={(on) => {
                      showDialog(
                        ({ close }) => (
                          <PublishWarningDialog
                            botId={props.botId}
                            close={close}
                            onSubmit={() => enableAiInsights({ enabled: on, showToast: true })}
                          />
                        ),
                        {
                          title: 'Warning',
                        }
                      )
                    }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
